import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Box,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { Save, CloudUpload } from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 900,
    margin: "0 auto"
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500
  },
  tabs: {
    marginBottom: theme.spacing(2),
    // Permite quebrar abas em várias linhas (sem rolagem horizontal).
    "& .MuiTabs-scroller": {
      overflow: "visible"
    },
    "& .MuiTabs-flexContainer": {
      flexWrap: "wrap"
    },
    "& .MuiTab-root": {
      minWidth: "auto",
      maxWidth: "100%",
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5)
    }
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500
  },
  form: {
    marginTop: theme.spacing(1)
  },
  textField: {
    marginBottom: theme.spacing(2)
  },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5)
  },
  button: {
    minWidth: 160
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4)
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    marginTop: -theme.spacing(0.5),
    marginBottom: theme.spacing(2)
  },
  brandingPreviewImg: {
    maxWidth: "100%",
    maxHeight: 120,
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    objectFit: "contain",
    background: "#fafafa"
  },
  uploadButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
}));

// helper pra montar URL da imagem (relativa ou absoluta)
const resolveImageUrl = (value) => {
  if (!value) return "";
  if (value.startsWith("http")) return value;

  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (!base) return value;

  const normalizedBase = base.replace(/\/+$/, "");
  const path = value.startsWith("/") ? value : `/${value}`;
  return `${normalizedBase}${path}`;
};

const GlobalConfig = () => {
  const classes = useStyles();

  const [tab, setTab] = useState("gateways");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({
    loginLogo: false,
    loginBackground: false
  });

  const [smtpTestOpen, setSmtpTestOpen] = useState(false);
  const [smtpTestTo, setSmtpTestTo] = useState("");
  const [smtpTestSending, setSmtpTestSending] = useState(false);

  const [config, setConfig] = useState({
    mpAccessToken: "",
    infinitePayHandle: "",
    mpGatewayEnabled: true,
    infinitePayGatewayEnabled: false,
    smtpHost: "",
    smtpPort: "",
    smtpSecure: "false",
    smtpUser: "",
    smtpPass: "",
    smtpFrom: "",
    trialExpiration: "",
    loginLogo: "",
    loginBackground: "",
    loginWhatsapp: "",
    uazapiServerUrl: "https://saczapi.uazapi.com",
    uazapiToken: ""
  });

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleChange = e => {
    const { name, value } = e.target;

    if (name === "trialExpiration") {
      const onlyDigits = value.replace(/\D/g, "");
      return setConfig(prev => ({ ...prev, [name]: onlyDigits }));
    }

    setConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put("/global-config", config);
      toast.success("Configurações salvas com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/global-config");
      setConfig(prev => ({
        ...prev,
        ...data,
        trialExpiration:
          data.trialExpiration !== undefined && data.trialExpiration !== null
            ? String(data.trialExpiration)
            : prev.trialExpiration
      }));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Se a aba atual ficar indisponível (gateway desativado), volta para "Gateways".
  useEffect(() => {
    if (tab === "infinite" && !config.infinitePayGatewayEnabled) {
      setTab("gateways");
    }
    if (tab === "mercado" && !config.mpGatewayEnabled) {
      setTab("gateways");
    }
  }, [tab, config.infinitePayGatewayEnabled, config.mpGatewayEnabled]);

  const handleOpenSmtpTest = () => {
    setSmtpTestTo("");
    setSmtpTestOpen(true);
  };

  const handleCloseSmtpTest = () => {
    if (!smtpTestSending) setSmtpTestOpen(false);
  };

  const handleSendSmtpTest = async () => {
    const to = (smtpTestTo || "").trim();
    if (!to) {
      toast.error("Informe o e-mail de destino.");
      return;
    }

    setSmtpTestSending(true);
    try {
      await api.post("/global-config/test-smtp", {
        to,
        smtpHost: config.smtpHost,
        smtpPort: config.smtpPort,
        smtpSecure: config.smtpSecure,
        smtpUser: config.smtpUser,
        smtpPass: config.smtpPass,
        smtpFrom: config.smtpFrom
      });
      toast.success("E-mail de teste enviado. Verifique a caixa de entrada.");
      setSmtpTestOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSmtpTestSending(false);
    }
  };

  const handleBrandingUpload = async (field, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("field", field); // "loginLogo" ou "loginBackground"

    try {
      setUploading(prev => ({ ...prev, [field]: true }));
      const { data } = await api.post("/global-config/upload", formData);

      // backend retorna { field, url }
      const url = data?.url || data?.[field];

      if (url) {
        setConfig(prev => ({
          ...prev,
          [field]: url
        }));
      }

      toast.success("Imagem atualizada com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  if (loading) {
    return (
      <Paper className={classes.root}>
        <div className={classes.loadingWrapper}>
          <CircularProgress />
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        Configurações Globais da Plataforma
      </Typography>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab value="gateways" label="Gateways de pagamento" />
        {config.infinitePayGatewayEnabled && (
          <Tab value="infinite" label="Infinite Pay" />
        )}
        {config.mpGatewayEnabled && <Tab value="mercado" label="Mercado Pago" />}
        <Tab value="smtp" label="E-mail (SMTP)" />
        <Tab value="trial" label="Trial / Assinatura" />
        <Tab value="login" label="Login / Capa" />
        <Tab value="uazapi" label="Uazapi" />
      </Tabs>

      <form onSubmit={handleSubmit} className={classes.form}>
        {/* ABA: INFINITE PAY */}
        {tab === "infinite" && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Infinite Pay
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Handle"
                  name="infinitePayHandle"
                  value={config.infinitePayHandle}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                />
                <div className={classes.helperText}>
                  Informe o Handle da Infinite Pay.
                </div>
              </Grid>
            </Grid>
          </>
        )}

        {/* ABA: GATEWAYS DE PAGAMENTO */}
        {tab === "gateways" && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Gateways de pagamento
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.mpGatewayEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setConfig((prev) => ({
                          ...prev,
                          mpGatewayEnabled: enabled,
                          infinitePayGatewayEnabled: enabled ? false : prev.infinitePayGatewayEnabled,
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label="Ativar Mercado Pago"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.infinitePayGatewayEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setConfig((prev) => ({
                          ...prev,
                          infinitePayGatewayEnabled: enabled,
                          mpGatewayEnabled: enabled ? false : prev.mpGatewayEnabled,
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label="Ativar Infinite Pay"
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* ABA: MERCADO PAGO */}
        {tab === "mercado" && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Mercado Pago
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Access Token"
                  name="mpAccessToken"
                  value={config.mpAccessToken}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                />
                <div className={classes.helperText}>
                  Use o Access Token do Mercado Pago da conta principal da
                  plataforma. As empresas clientes usarão sempre essa
                  configuração.
                </div>
              </Grid>
            </Grid>
          </>
        )}

        {/* ABA: SMTP */}
        {tab === "smtp" && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              E-mail (SMTP)
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Host"
                  name="smtpHost"
                  value={config.smtpHost}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="Porta"
                  name="smtpPort"
                  value={config.smtpPort}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="Secure (SSL/TLS)"
                  name="smtpSecure"
                  value={config.smtpSecure}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  helperText={`"true" para conexão segura, "false" para STARTTLS/normal`}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="E-mail"
                  name="smtpUser"
                  value={config.smtpUser}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Senha de app"
                  name="smtpPass"
                  value={config.smtpPass}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  type="password"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Remetente (FROM)"
                  name="smtpFrom"
                  value={config.smtpFrom}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  helperText={`Exemplo: Sua empresa <suporte@gmail.com>`}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* ABA: TRIAL */}
        {tab === "trial" && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Trial / Período de Teste
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Dias de teste"
                  name="trialExpiration"
                  value={config.trialExpiration}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                />
                <div className={classes.helperText}>
                  Quantidade de dias de teste que a empresa nova terá. Se
                  vazio, o sistema usa o valor padrão do .env (APP_TRIALEXPIRATION,
                  ex.: 3).
                </div>
              </Grid>
            </Grid>
          </>
        )}

        {/* ABA: LOGIN / CAPA */}
        {tab === "login" && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Login / capa
            </Typography>

            <Grid container spacing={3}>
              {/* LOGO DO LOGIN (apenas upload + preview) */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Logo do login</Typography>

                {config.loginLogo && (
                  <Box mt={1} mb={1}>
                    <img
                      src={resolveImageUrl(config.loginLogo)}
                      alt="Logo do login"
                      className={classes.brandingPreviewImg}
                    />
                  </Box>
                )}

                <input
                  id="loginLogoUpload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    handleBrandingUpload("loginLogo", e.target.files[0])
                  }
                />
                <label htmlFor="loginLogoUpload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    className={classes.uploadButton}
                    disabled={uploading.loginLogo}
                  >
                    {uploading.loginLogo ? "Enviando..." : "Enviar logo"}
                  </Button>
                </label>

                <div className={classes.helperText}>
                  Se nenhuma imagem for enviada, o sistema usa <code>/logo.png</code>.
                </div>
              </Grid>

              {/* CAPA / BACKGROUND DO LOGIN (apenas upload + preview) */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">
                  Imagem de fundo (capa do login)
                </Typography>

                {config.loginBackground && (
                  <Box mt={1} mb={1}>
                    <img
                      src={resolveImageUrl(config.loginBackground)}
                      alt="Capa do login"
                      className={classes.brandingPreviewImg}
                    />
                  </Box>
                )}

                <input
                  id="loginBackgroundUpload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    handleBrandingUpload("loginBackground", e.target.files[0])
                  }
                />
                <label htmlFor="loginBackgroundUpload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    className={classes.uploadButton}
                    disabled={uploading.loginBackground}
                  >
                    {uploading.loginBackground ? "Enviando..." : "Enviar capa"}
                  </Button>
                </label>

                <div className={classes.helperText}>
                  Recomendada imagem em <code>.webp</code> ou <code>.jpg</code>. 
                  Se vazio, o sistema usa a capa padrão.
                </div>
              </Grid>

              {/* LINK DO WHATSAPP DO LOGIN (mantém campo de texto) */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Link do WhatsApp do login"
                  name="loginWhatsapp"
                  value={config.loginWhatsapp}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                />
                <div className={classes.helperText}>
                  Exemplo: <code>https://wa.me/5541999999999</code>. Usado no botão
                  de WhatsApp da tela de login e no botão "Chamar suporte".
                </div>
              </Grid>
            </Grid>
          </>
        )}

        {/* ABA: UAZAPI */}
        {tab === "uazapi" && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Uazapi
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="URL do servidor (Uazapi)"
                  name="uazapiServerUrl"
                  value={config.uazapiServerUrl}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  placeholder="https://saczapi.uazapi.com"
                  autoComplete="off"
                />
                <div className={classes.helperText}>
                  Endereço base da API Uazapi usado pelo backend para enviar mensagens e
                  health check (ex.: <code>https://saczapi.uazapi.com</code>).
                </div>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Token"
                  name="uazapiToken"
                  value={config.uazapiToken}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  className={classes.textField}
                  size="small"
                  type="password"
                  autoComplete="off"
                />
                <div className={classes.helperText}>
                  Usado como <strong>admintoken</strong> na API Uazapi (criar instâncias) e
                  como fallback quando a conexão não tem token próprio.
                </div>
              </Grid>
            </Grid>
          </>
        )}

        <div className={classes.actions}>
          {tab === "smtp" && (
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={handleOpenSmtpTest}
            >
              Enviar e-mail de teste
            </Button>
          )}
          <Button
            type="submit"
            color="primary"
            variant="contained"
            className={classes.button}
            startIcon={!saving && <Save />}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </div>
      </form>

      <Dialog
        open={smtpTestOpen}
        onClose={handleCloseSmtpTest}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Testar SMTP</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Informe o e-mail que deve receber a mensagem de teste para validar
            as credenciais SMTP configuradas nesta aba.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="E-mail de destino"
            type="email"
            fullWidth
            variant="outlined"
            value={smtpTestTo}
            onChange={e => setSmtpTestTo(e.target.value)}
            disabled={smtpTestSending}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSmtpTest} disabled={smtpTestSending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSendSmtpTest}
            color="primary"
            variant="contained"
            disabled={smtpTestSending}
          >
            {smtpTestSending ? <CircularProgress size={22} /> : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GlobalConfig;
